import type { HeroActivity } from '@/modules/game-domain/hero-activity.types';
import type { TownLayoutPayload } from '@/modules/game-domain/authored-town.types';
import type { ServerActiveBuffRow, ServerActiveDebuffRow } from '@/network/buffMap';
import { ApiError, apiGet, apiPost, apiGetWithEtag } from '@/network/api-client';

export interface HeroResponse {
  id: number;
  telegramId: number;
  name: string;
  modelVariant?: number;
  hp: number;
  maxHp: number;
  attack: number;
  defense: number;
  speed: number;
  attackSpeed?: number;
  attackPower?: number;
  defensePower?: number;
  strength: number;
  constitution: number;
  agility: number;
  luck: number;
  state: string;
  currentActivity?: HeroActivity;
  activeCrierSpeech?: unknown;
  restKind?: string;
  excursionPhase?: string;
  excursionKind?: string;
  townTourPhase?: string;
  townTourNpcId?: number;
  townTourExitPending?: boolean;
  weaponId?: number;
  armorId?: number;
  weapon?: WeaponResponse | null;
  armor?: ArmorResponse | null;
  gold: number;
  xp: number;
  xpToNext?: number;
  level: number;
  /** Server capability; omitted by legacy servers with finite revive quotas. */
  manualRevivesUnlimited?: boolean;
  reviveCount?: number;
  subscriptionActive?: boolean;
  buffFreeChargesRemaining?: number;
  buffQuotaPeriodEnd?: string;
  buffCharges?: Record<string, { remaining: number; periodEnd: string | null; max?: number }>;
  potions?: number;
  positionX?: number;
  positionY?: number;
  currentTownId?: number;
  moveSpeed?: number;
  buffs?: ServerActiveBuffRow[];
  debuffs?: ServerActiveDebuffRow[];
  debuffCatalog?: Record<string, { name?: string; durationMs: number; magnitude?: number }>;
  equipment?: Record<string, EquipmentItemResponse>;
  equipmentState?: { revision: number };
  gear?: HeroResponse['equipment'];
  inventory?: Array<{
    id: number;
    slot: string;
    formId?: string;
    name: string;
    rarity: string;
    ilvl?: number;
    primaryStat?: number;
    statType?: string;
  }>;
}

export interface AuthResponse {
  token: string;
  heroId: number;
}

export interface OfflineLootLine {
  itemType: string;
  itemId?: number;
  itemName?: string;
  rarity: string;
  goldAmount?: number;
}

export interface OfflineReport {
  offlineSeconds: number;
  monstersKilled: number;
  xpGained: number;
  goldGained: number;
  levelsGained: number;
  deaths?: number;
  revives?: number;
  loot?: OfflineLootLine[];
  message?: string;
}

export interface ChangelogContent {
  title: string;
  items: string[];
}

export interface ChangelogPayload extends ChangelogContent {
  localized?: Partial<Record<'ru' | 'en', ChangelogContent>>;
}

export interface InitTownPayload {
  id: number;
  name: string;
  nameKey?: string;
  biome: string;
  worldX: number;
  worldY: number;
  radius: number;
  levelMin?: number;
  levelMax?: number;
  size?: string;
  npcs?: Array<{
    id: number;
    name: string;
    nameKey?: string;
    type: string;
    worldX: number;
    worldY: number;
    buildingId?: number;
  }>;
}

export interface RoadSegmentPayload {
  fromTownId: number;
  toTownId: number;
  waypoints: Array<{ x: number; y: number }>;
  distanceWorldUnits?: number;
  /** Nominal real-world seconds at BaseMoveSpeed; excludes speed bonuses, encounters and stops. */
  estimatedTravelSeconds?: number;
  /** Whether the backend route planner considers this edge within its travel band. */
  routeEligible?: boolean;
}

export interface RoadSettlementPayload {
  layout?: TownLayoutPayload;
  id: number;
  roadId: number;
  eventId: string;
  name: string;
  worldX: number;
  worldY: number;
  radius: number;
  progress?: number;
  awayX?: number;
  awayY?: number;
  spineX?: number;
  spineY?: number;
  entryX?: number;
  entryY?: number;
  tangentX?: number;
  tangentY?: number;
  connectorWaypoints?: Array<{ x: number; y: number }>;
  buildings?: Array<{
    id: number;
    buildingType: string;
    variant?: number;
    worldX: number;
    worldY: number;
    facing?: string;
    footprintW?: number;
    footprintH?: number;
  }>;
  npcs?: Array<{
    id: number;
    name: string;
    nameKey?: string;
    type: string;
    worldX: number;
    worldY: number;
  }>;
}

export interface InitHeroResponse {
  hero: HeroResponse | null;
  offlineReport: OfflineReport | null;
  mapRef: MapRefResponse;
  towns?: InitTownPayload[];
  roads?: RoadSegmentPayload[];
  roadSettlements?: RoadSettlementPayload[];
  needsName?: boolean;
  npcCostPotion?: number;
  npcCostHeal?: number;
  npcCostPotionBase?: number;
  npcCostHealBase?: number;
  npcCostPotionPerLevel?: number;
  npcCostHealPerLevel?: number;
  merchantTownGearCostBase?: number;
  merchantTownGearCostPerTownLevel?: number;
  serverVersion?: string;
  showChangelog?: boolean;
  changelog?: ChangelogPayload | null;
}

export interface MapRefResponse {
  mapId: string;
  mapVersion: string;
  etag: string;
  biome: string;
  recommendedLevelMin: number;
  recommendedLevelMax: number;
}

export interface MapTileResponse {
  x: number;
  y: number;
  terrain: string;
}

export interface MapObjectResponse {
  id: string;
  type: string;
  x: number;
  y: number;
}

export interface SpawnPointResponse {
  id: string;
  kind: string;
  x: number;
  y: number;
}

export interface ServerMapResponse {
  mapId: string;
  mapVersion: string;
  biome: string;
  recommendedLevelMin: number;
  recommendedLevelMax: number;
  width: number;
  height: number;
  tiles: MapTileResponse[];
  objects: MapObjectResponse[];
  spawnPoints: SpawnPointResponse[];
}

export interface GetMapResult {
  map: ServerMapResponse | null;
  etag: string;
  notModified: boolean;
}

export interface LootResponse {
  itemType: 'weapon' | 'armor' | 'gold';
  itemName?: string;
  rarity: string;
  goldAmount: number;
}

export interface WeaponResponse {
  id: number;
  name: string;
  type: string;
  rarity: string;
  damage: number;
  speedMultiplier: number;
  ilvl?: number;
}

export interface ArmorResponse {
  id: number;
  name: string;
  type: string;
  rarity: string;
  defense: number;
  speedMultiplier: number;
  ilvl?: number;
}

export interface EquipmentItemResponse {
  id: number;
  slot: string;
  formId: string;
  name: string;
  rarity: string;
  ilvl: number;
  basePrimary: number;
  primaryStat: number;
  statType: string;
  subtype?: string;
}

export interface LogEntry {
  id: number;
  message: string;
  createdAt: string;
  event?: { code: string; args?: Record<string, unknown> };
}

export interface NearbyNPCResponse {
  id: number;
  name: string;
  type: string;
  worldX: number;
  worldY: number;
}

export interface NPCInteractResponse {
  npcName: string;
  npcType: string;
  actions: Array<{ type: string; label: string; cost?: number }>;
  quests?: import('@/game/types').Quest[];
}

export interface NPCAlmsResponse {
  accepted: boolean;
  message: string;
  goldSpent?: number;
  itemDrop?: {
    itemType: string;
    itemId?: number;
    itemName?: string;
    rarity: string;
    goldAmount?: number;
  };
  hero?: HeroResponse;
}

export interface DailyTaskResponse {
  taskId: string;
  title: string;
  description: string;
  objectiveType: string;
  objectiveCount: number;
  progress: number;
  completed: boolean;
  claimed: boolean;
  rewardType: string;
  rewardAmount: number;
  period: string;
}

export function offlineLootLinesWithoutGold(lines: OfflineLootLine[] | undefined): OfflineLootLine[] {
  if (!lines?.length) return [];
  return lines.filter((l) => l.itemType !== 'gold');
}

export function offlineReportHasActivity(r: OfflineReport): boolean {
  return (
    r.monstersKilled > 0 ||
    r.xpGained > 0 ||
    r.goldGained > 0 ||
    r.levelsGained > 0 ||
    (r.deaths ?? 0) > 0 ||
    (r.revives ?? 0) > 0 ||
    offlineLootLinesWithoutGold(r.loot).length > 0 ||
    Boolean(r.message?.trim())
  );
}

export function formatOfflineLootLine(line: OfflineLootLine): string {
  const name = line.itemName?.trim() || line.itemType;
  return `${name} (${line.rarity})`;
}

export async function authenticate(): Promise<AuthResponse> {
  return apiPost<AuthResponse>('/auth/telegram');
}

export async function getHero(telegramId?: number, signal?: AbortSignal): Promise<HeroResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiGet<HeroResponse>(`/hero${query}`, signal);
}

function isTransientInitError(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 503) return false;
  try {
    const body: unknown = JSON.parse(error.body);
    if (typeof body !== 'object' || body === null || !('error' in body)) return false;
    return body.error === 'biography_unavailable' || body.error === 'hero simulation unavailable';
  } catch {
    return false;
  }
}

function waitForInitRetry(delay: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Hero initialization aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      reject(new DOMException('Hero initialization aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, delay);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export async function initHero(telegramId?: number, signal?: AbortSignal): Promise<InitHeroResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  const delays = [100, 250, 500];
  for (let attempt = 0; ; attempt++) {
    if (signal?.aborted) throw new DOMException('Hero initialization aborted', 'AbortError');
    try {
      return await apiGet<InitHeroResponse>(`/hero/init${query}`, signal);
    } catch (error) {
      if (signal?.aborted) throw new DOMException('Hero initialization aborted', 'AbortError');
      const delay = delays[attempt];
      if (delay === undefined || !isTransientInitError(error)) throw error;
      await waitForInitRetry(delay, signal);
    }
  }
}

export async function ackChangelog(telegramId?: number): Promise<void> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  await apiPost<{ ok?: boolean }>(`/hero/changelog/ack${query}`);
}

export async function setHeroName(name: string, telegramId?: number, modelVariant?: 0 | 1 | 2 | 3): Promise<HeroResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<HeroResponse>(`/hero/name${query}`, { name, ...(modelVariant !== undefined ? { modelVariant } : {}) });
}

export async function getMap(mapId: string, etag?: string): Promise<GetMapResult> {
  const { data, etag: responseEtag, notModified } = await apiGetWithEtag<ServerMapResponse>(
    `/maps/${encodeURIComponent(mapId)}`,
    etag,
  );
  return { map: data, etag: responseEtag, notModified };
}

export async function requestRevive(telegramId?: number): Promise<HeroResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<HeroResponse>(`/hero/revive${query}`);
}

export async function getHeroEquipment(telegramId?: number): Promise<Record<string, EquipmentItemResponse>> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiGet<Record<string, EquipmentItemResponse>>(`/hero/equipment${query}`);
}

export async function getLoot(): Promise<LootResponse[]> {
  return apiGet<LootResponse[]>('/hero/loot');
}

export async function getWeapons(): Promise<WeaponResponse[]> {
  return apiGet<WeaponResponse[]>('/weapons');
}

export async function getArmor(): Promise<ArmorResponse[]> {
  return apiGet<ArmorResponse[]>('/armor');
}

export async function getAdventureLog(telegramId?: number, limit?: number): Promise<LogEntry[]> {
  const params = new URLSearchParams();
  if (telegramId != null) params.set('telegramId', String(telegramId));
  if (limit != null) params.set('limit', String(limit));
  const query = params.toString() ? `?${params.toString()}` : '';
  const raw = await apiGet<{ log?: LogEntry[] } | LogEntry[]>(`/hero/log${query}`);
  if (Array.isArray(raw)) return raw;
  return raw.log ?? [];
}

export async function getNearbyNPCs(
  telegramId?: number,
  posX?: number,
  posY?: number,
): Promise<NearbyNPCResponse[]> {
  const params = new URLSearchParams();
  if (telegramId != null) params.set('telegramId', String(telegramId));
  if (posX != null) params.set('posX', String(posX));
  if (posY != null) params.set('posY', String(posY));
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiGet<NearbyNPCResponse[]>(`/hero/nearby-npcs${query}`);
}

export async function interactWithNPC(npcId: number, telegramId?: number): Promise<NPCInteractResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<NPCInteractResponse>(`/npcs/${npcId}/interact${query}`);
}

export async function buyFromMerchant(npcId: number, item: string, telegramId?: number): Promise<HeroResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<HeroResponse>(`/npcs/${npcId}/buy${query}`, { item });
}

export async function healAtHealerNPC(npcId: number, telegramId?: number): Promise<HeroResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<HeroResponse>(`/npcs/${npcId}/heal${query}`);
}

export async function giveNPCAlms(telegramId?: number): Promise<NPCAlmsResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<NPCAlmsResponse>(`/hero/npc-alms${query}`, { accept: true });
}

export interface PaymentInvoice { invoiceUrl: string; currency: 'XTR'; totalAmount: number }

export async function createPaymentInvoice(type: 'buff_refill' | 'resurrection_refill' | 'subscription_weekly', buffType?: string, telegramId?: number): Promise<PaymentInvoice> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<PaymentInvoice>(`/payments/create-invoice${query}`, { type, buffType });
}

export async function getDailyTasks(telegramId?: number): Promise<DailyTaskResponse[]> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiGet<DailyTaskResponse[]>(`/hero/daily-tasks${query}`);
}

export async function claimDailyTask(taskId: string, telegramId?: number): Promise<HeroResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<HeroResponse>(`/hero/daily-tasks/${encodeURIComponent(taskId)}/claim${query}`);
}

export async function buyPotion(
  body: { npcId: number; positionX: number; positionY: number; expectedGoldCost?: number; commandId?: string },
  telegramId?: number,
): Promise<HeroResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<HeroResponse>(`/hero/npc-buy-potion${query}`, body);
}

export async function healAtNPC(
  body: { npcId: number; positionX: number; positionY: number; expectedGoldCost?: number; commandId?: string },
  telegramId?: number,
): Promise<HeroResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<HeroResponse>(`/hero/npc-heal${query}`, body);
}

export async function setNPCDialogPause(open: boolean, telegramId?: number): Promise<void> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  await apiPost<{ ok: boolean }>(`/hero/npc-dialog-pause${query}`, { open });
}
